import { useReducer } from "react";
import {
  initialReportEmailDeliveryForm,
  isReportEmailDeliveryFormDirty,
  isReportEmailDeliveryFormSeededFrom,
  reportEmailDeliveryFormReducer,
  reportEmailDeliveryFormValue,
  type ReportEmailDeliveryFrequencyName,
} from "./report-email-delivery-form";
import type { IsoWeekday, ReportEmailDeliveryFrequency } from "../../client-messages";

export interface ReportEmailDeliveryFormModel {
  readonly frequency: ReportEmailDeliveryFrequencyName;
  /** Kept while daily, so switching back to weekly restores it. */
  readonly weekday: IsoWeekday;
  readonly setFrequency: (frequency: ReportEmailDeliveryFrequencyName) => void;
  readonly setWeekday: (weekday: IsoWeekday) => void;
  /** Whether `value` differs from `initial`. */
  readonly isDirty: boolean;
  /** Exactly the PUT body: no weekday when daily. */
  readonly value: ReportEmailDeliveryFrequency;
  /** Drops unsaved changes, back to `initial`. */
  readonly reset: () => void;
}

/**
 * Form state for setting up a report email delivery. A new delivery starts
 * weekly on Monday; pass the saved delivery to edit it instead.
 *
 * When `initial` changes (a save, or a stop that clears it), the form starts
 * over from the new value, so it is clean again and `isDirty` compares against
 * what is now saved.
 */
export function useReportEmailDeliveryForm(initial?: ReportEmailDeliveryFrequency): ReportEmailDeliveryFormModel {
  const [state, dispatch] = useReducer(reportEmailDeliveryFormReducer, initial, initialReportEmailDeliveryForm);
  if (!isReportEmailDeliveryFormSeededFrom(state, initial)) {
    // A render-phase update: React re-renders straight away with the reseeded
    // state, before anything is committed with the stale one.
    dispatch({ type: "reseed", initial });
  }
  return {
    frequency: state.frequency,
    weekday: state.weekday,
    setFrequency: (frequency) => dispatch({ type: "setFrequency", frequency }),
    setWeekday: (weekday) => dispatch({ type: "setWeekday", weekday }),
    isDirty: isReportEmailDeliveryFormDirty(state),
    value: reportEmailDeliveryFormValue(state),
    reset: () => dispatch({ type: "reseed", initial }),
  };
}
